// HRV 기반 증상 예측 함수들

import { WindowData, PSDData } from './types';

/**
 * 증상 예측 결과 인터페이스
 */
export interface SymptomPrediction {
  symptomName: string;
  riskLevel: 'low' | 'moderate' | 'high' | 'very_high';
  score: number; // 0-100
  confidence: number; // 0-1
  indicators: string[];
  description: string;
  icon: string;
  metrics?: { [key: string]: number | string };
}

/**
 * 전체 증상 예측 결과
 */
export interface SymptomPredictions {
  stress: SymptomPrediction;
  breathing: SymptomPrediction;
  pain: SymptomPrediction;
  depression: SymptomPrediction;
  cardiovascular: SymptomPrediction;
  bloodPressure: SymptomPrediction;
}

/**
 * 중앙값 계산
 */
function calculateMedian(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 
    ? (sorted[mid - 1] + sorted[mid]) / 2 
    : sorted[mid];
}

/**
 * 표준편차 계산
 */
function calculateStd(arr: number[]): number {
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
  const variance = arr.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / arr.length;
  return Math.sqrt(variance);
}

/**
 * 1. 스트레스 예측
 */
export function predictStress(windows: WindowData[], psd: PSDData): SymptomPrediction {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  
  if (validWindows.length < 5) {
    return createInsufficientDataPrediction('mentalHealth.symptom.stress', '🧠');
  }

  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  
  const hrMed = calculateMedian(hrValues);
  const rmssdMed = calculateMedian(rmssdValues);
  const lfhf = psd.LF_HF;

  // 스트레스 점수 계산 (0-100)
  // 스트레스 점수 계산 (0-100)
  let stressScore = 0;
  const indicators: string[] = [];

  // 심박수 증가 (가중치: 30%)
  if (hrMed > 90) {
    stressScore += 30;
    indicators.push('symptom.indicator.highHeartRate');
  } else if (hrMed > 80) {
    stressScore += 20;
    indicators.push('symptom.indicator.slightlyHighHeartRate');
  } else if (hrMed > 70) {
    stressScore += 10;
  }

  // RMSSD 감소 (가중치: 40%)
  if (rmssdMed < 20) {
    stressScore += 40;
    indicators.push('symptom.indicator.veryLowHRV');
  } else if (rmssdMed < 30) {
    stressScore += 30;
    indicators.push('symptom.indicator.lowHRV');
  } else if (rmssdMed < 40) {
    stressScore += 15;
    indicators.push('symptom.indicator.slightlyLowHRV');
  }

  // LF/HF 비율 증가 (가중치: 30%)
  if (isFinite(lfhf)) {
    if (lfhf > 3.0) {
      stressScore += 30;
      indicators.push('symptom.indicator.sympatheticOveractive');
    } else if (lfhf > 2.0) {
      stressScore += 20;
      indicators.push('symptom.indicator.sympatheticDominant');
    } else if (lfhf > 1.5) {
      stressScore += 10;
    }
  }

  const riskLevel = getRiskLevel(stressScore);
  const description = getStressDescription(stressScore, indicators);
  const confidence = calculateConfidence(validWindows.length, 20);

  return {
    symptomName: 'mentalHealth.symptom.stress',
    riskLevel,
    score: stressScore,
    confidence,
    indicators: indicators.length > 0 ? indicators : ['symptom.indicator.normalRange'],
    description,
    icon: '🧠',
    metrics: {
      "평균 심박수": hrMed.toFixed(1) + " bpm",
      "심박변이도 (RMSSD)": rmssdMed.toFixed(1) + " ms",
      "자율신경 균형 (LF/HF)": isFinite(lfhf) ? lfhf.toFixed(2) : "N/A"
    }
  };
}

/**
 * 2. 호흡 관련 증상 예측
 */
export function predictBreathing(windows: WindowData[]): SymptomPrediction {
  const validWindows = windows.filter(w => w.DQI >= 0.6);
  
  if (validWindows.length < 5) {
    return createInsufficientDataPrediction('mentalHealth.symptom.breathing', '🫁');
  }

  const respValues = validWindows.map(w => w.Resp_bpm).filter(v => isFinite(v));
  const respQValues = validWindows.map(w => w.RespQ).filter(v => isFinite(v));
  
  const respMed = calculateMedian(respValues);
  const respStd = calculateStd(respValues);
  const respQMed = calculateMedian(respQValues);

  let breathingScore = 0;
  const indicators: string[] = [];

  // 호흡수 이상 (가중치: 40%)
  if (respMed > 24 || respMed < 8) {
    breathingScore += 40;
    indicators.push(respMed > 24 ? 'symptom.indicator.fastBreathing' : 'symptom.indicator.slowBreathing');
  } else if (respMed > 20 || respMed < 10) {
    breathingScore += 25;
    indicators.push('symptom.indicator.irregularBreathing');
  } else if (respMed > 18 || respMed < 12) {
    breathingScore += 10;
  }

  // 호흡 변동성 (가중치: 30%)
  if (respStd > 5.0) {
    breathingScore += 30;
    indicators.push('symptom.indicator.irregularBreathingPattern');
  } else if (respStd > 3.0) {
    breathingScore += 15;
    indicators.push('symptom.indicator.somewhatIrregularBreathing');
  }

  // 호흡 품질 (가중치: 30%)
  if (respQMed < 0.5) {
    breathingScore += 30;
    indicators.push('symptom.indicator.lowBreathingQuality');
  } else if (respQMed < 0.7) {
    breathingScore += 15;
  }

  const riskLevel = getRiskLevel(breathingScore);
  const description = getBreathingDescription(breathingScore, respMed);

  return {
    symptomName: 'mentalHealth.symptom.breathing',
    riskLevel,
    score: breathingScore,
    confidence: calculateConfidence(validWindows.length, 20),
    indicators: indicators.length > 0 ? indicators : ['symptom.indicator.normalRange'],
    description,
    icon: '🫁',
    metrics: {
      "평균 호흡수": respMed.toFixed(1) + " rpm",
      "호흡 안정성": respStd.toFixed(1),
      "호흡 품질": respQMed.toFixed(2)
    }
  };
}

/**
 * 3. 통증 예측
 */
export function predictPain(windows: WindowData[], psd: PSDData): SymptomPrediction {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  
  if (validWindows.length < 5) {
    return createInsufficientDataPrediction('mentalHealth.symptom.pain', '😣');
  }

  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  
  const rmssdMed = calculateMedian(rmssdValues);
  const hrMed = calculateMedian(hrValues);
  const lfnu = psd.LFnu;

  let painScore = 0;
  const indicators: string[] = [];

  // RMSSD 감소 (통증 시 HRV 감소) (가중치: 40%)
  if (rmssdMed < 15) {
    painScore += 40;
    indicators.push('symptom.indicator.severeAutonomicSuppression');
  } else if (rmssdMed < 25) {
    painScore += 25;
    indicators.push('symptom.indicator.autonomicActivityLow');
  } else if (rmssdMed < 35) {
    painScore += 10;
  }

  // 교감신경 과활성 (가중치: 35%)
  if (isFinite(lfnu) && lfnu > 70) {
    painScore += 35;
    indicators.push('symptom.indicator.sympatheticOveractive');
  } else if (isFinite(lfnu) && lfnu > 60) {
    painScore += 20;
    indicators.push('symptom.indicator.sympatheticDominant');
  }

  // 심박수 증가 (가중치: 25%)
  if (hrMed > 85) {
    painScore += 25;
    indicators.push('symptom.indicator.persistentHighHeartRate');
  } else if (hrMed > 75) {
    painScore += 12;
  }

  const riskLevel = getRiskLevel(painScore);
  const description = getPainDescription(painScore);

  return {
    symptomName: 'mentalHealth.symptom.pain',
    riskLevel,
    score: painScore,
    confidence: calculateConfidence(validWindows.length, 20),
    indicators: indicators.length > 0 ? indicators : ['symptom.indicator.normalRange'],
    description,
    icon: '😣',
    metrics: {
      "심박변이도 (RMSSD)": rmssdMed.toFixed(1) + " ms",
      "평균 심박수": hrMed.toFixed(1) + " bpm",
      "교감신경 활성도 (LFnu)": isFinite(lfnu) ? lfnu.toFixed(1) + "%" : "N/A"
    }
  };
}

/**
 * 4. 우울증 예측
 */
export function predictDepression(windows: WindowData[], psd: PSDData): SymptomPrediction {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  
  if (validWindows.length < 5) {
    return createInsufficientDataPrediction('mentalHealth.symptom.depression', '😔');
  }

  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  
  const rmssdMed = calculateMedian(rmssdValues);
  const rmssdStd = calculateStd(rmssdValues);
  const hrMed = calculateMedian(hrValues);
  const tp = psd.TP;

  let depressionScore = 0;
  const indicators: string[] = [];

  // 전체적인 HRV 감소 (가중치: 40%)
  if (rmssdMed < 20) {
    depressionScore += 40;
    indicators.push('symptom.indicator.remarkablyLowHRV');
  } else if (rmssdMed < 30) {
    depressionScore += 25;
    indicators.push('symptom.indicator.lowHRV');
  } else if (rmssdMed < 40) {
    depressionScore += 10;
  }

  // HRV 변동성 감소 (우울증 특징) (가중치: 30%)
  if (rmssdStd < 5) {
    depressionScore += 30;
    indicators.push('symptom.indicator.hrvReactivityDecrease');
  } else if (rmssdStd < 10) {
    depressionScore += 15;
  }

  // Total Power 감소 (가중치: 20%)
  if (isFinite(tp) && tp < 500) {
    depressionScore += 20;
    indicators.push('symptom.indicator.overallAutonomicActivityLow');
  } else if (isFinite(tp) && tp < 1000) {
    depressionScore += 10;
  }

  // 안정 시 심박수 이상 (가중치: 10%)
  if (hrMed > 85 || hrMed < 55) {
    depressionScore += 10;
    indicators.push('symptom.indicator.abnormalRestingHeartRate');
  }

  const riskLevel = getRiskLevel(depressionScore);
  const description = getDepressionDescription(depressionScore);

  return {
    symptomName: 'mentalHealth.symptom.depression',
    riskLevel,
    score: depressionScore,
    confidence: calculateConfidence(validWindows.length, 20) * 0.8, // 우울증은 신뢰도 낮춤
    indicators: indicators.length > 0 ? indicators : ['symptom.indicator.normalRange'],
    description,
    icon: '😔',
    metrics: {
      "심박변이도 (RMSSD)": rmssdMed.toFixed(1) + " ms",
      "HRV 안정성 (RMSSD Std)": rmssdStd.toFixed(1),
      "총 파워 (TP)": isFinite(tp) ? tp.toFixed(0) : "N/A"
    }
  };
}

/**
 * 5. 심장 질환 예측
 */
export function predictCardiovascular(windows: WindowData[], psd: PSDData): SymptomPrediction {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  
  if (validWindows.length < 5) {
    return createInsufficientDataPrediction('mentalHealth.symptom.cardiovascular', '❤️');
  }

  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  const snrValues = validWindows.map(w => w.SNR).filter(v => isFinite(v));
  
  const hrMed = calculateMedian(hrValues);
  const hrStd = calculateStd(hrValues);
  const rmssdMed = calculateMedian(rmssdValues);
  const snrMed = calculateMedian(snrValues);
  const hf = psd.HF;

  let cvdScore = 0;
  const indicators: string[] = [];

  // 심박수 이상 (가중치: 30%)
  if (hrMed > 100 || hrMed < 50) {
    cvdScore += 30;
    indicators.push(hrMed > 100 ? 'symptom.indicator.tachycardiaRisk' : 'symptom.indicator.bradycardiaRisk');
  } else if (hrMed > 90 || hrMed < 55) {
    cvdScore += 15;
    indicators.push('symptom.indicator.borderlineHeartRate');
  }

  // 심박 변동성 (가중치: 25%)
  if (hrStd > 15) {
    cvdScore += 25;
    indicators.push('symptom.indicator.heartRateIrregularity');
  } else if (hrStd > 10) {
    cvdScore += 12;
  }

  // RMSSD 심각한 저하 (가중치: 25%)
  if (rmssdMed < 15) {
    cvdScore += 25;
    indicators.push('symptom.indicator.severeAutonomicDysfunction');
  } else if (rmssdMed < 25) {
    cvdScore += 12;
  }

  // HF 감소 (부교감 활성 저하) (가중치: 15%)
  if (isFinite(hf) && hf < 50) {
    cvdScore += 15;
    indicators.push('symptom.indicator.parasympatheticActivityLow');
  } else if (isFinite(hf) && hf < 100) {
    cvdScore += 7;
  }

  // 신호 품질 저하 (가중치: 5%)
  if (snrMed < 3) {
    cvdScore += 5;
    indicators.push('symptom.indicator.signalQualityDegraded');
  }

  const riskLevel = getRiskLevel(cvdScore);
  const description = getCardiovascularDescription(cvdScore);

  return {
    symptomName: 'mentalHealth.symptom.cardiovascular',
    riskLevel,
    score: cvdScore,
    confidence: calculateConfidence(validWindows.length, 20) * 0.85, // 의료 진단이므로 신뢰도 낮춤
    indicators: indicators.length > 0 ? indicators : ['symptom.indicator.normalRange'],
    description,
    icon: '❤️',
    metrics: {
      "평균 심박수": hrMed.toFixed(1) + " bpm",
      "심박 안정성 (HR Std)": hrStd.toFixed(1),
      "심박변이도 (RMSSD)": rmssdMed.toFixed(1) + " ms",
      "부교감신경 활성도 (HF)": isFinite(hf) ? hf.toFixed(0) : "N/A"
    }
  };
}

/**
 * 6. 혈압 이상 예측
 */
export function predictBloodPressure(windows: WindowData[], psd: PSDData): SymptomPrediction {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  
  if (validWindows.length < 5) {
    return createInsufficientDataPrediction('mentalHealth.symptom.bloodPressure', '💉');
  }

  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  
  const hrMed = calculateMedian(hrValues);
  const rmssdMed = calculateMedian(rmssdValues);
  const lfhf = psd.LF_HF;
  const vlf = psd.VLF;

  let bpScore = 0;
  const indicators: string[] = [];

  // LF/HF 비율 증가 (교감 우세, 고혈압 관련) (가중치: 35%)
  if (isFinite(lfhf)) {
    if (lfhf > 3.5) {
      bpScore += 35;
      indicators.push('symptom.indicator.sympatheticOveractiveHypertensionRisk');
    } else if (lfhf > 2.5) {
      bpScore += 20;
      indicators.push('symptom.indicator.sympatheticDominant');
    } else if (lfhf < 0.5) {
      bpScore += 15;
      indicators.push('symptom.indicator.parasympatheticDominantHypotensionRisk');
    }
  }

  // VLF 증가 (혈압 조절 이상) (가중치: 25%)
  if (isFinite(vlf) && vlf > 1000) {
    bpScore += 25;
    indicators.push('symptom.indicator.bloodPressureRegulationUnstable');
  } else if (isFinite(vlf) && vlf > 700) {
    bpScore += 12;
  }

  // 심박수 증가 (가중치: 25%)
  if (hrMed > 85) {
    bpScore += 25;
    indicators.push('symptom.indicator.highHeartRateBloodPressureRise');
  } else if (hrMed > 75) {
    bpScore += 12;
  }

  // RMSSD 감소 (가중치: 15%)
  if (rmssdMed < 25) {
    bpScore += 15;
    indicators.push('symptom.indicator.autonomicRegulationDysfunction');
  } else if (rmssdMed < 35) {
    bpScore += 7;
  }

  const riskLevel = getRiskLevel(bpScore);
  const description = getBloodPressureDescription(bpScore, lfhf);

  return {
    symptomName: 'mentalHealth.symptom.bloodPressure',
    riskLevel,
    score: bpScore,
    confidence: calculateConfidence(validWindows.length, 20) * 0.75, // 간접 측정이므로 신뢰도 낮춤
    indicators: indicators.length > 0 ? indicators : ['symptom.indicator.normalRange'],
    description,
    icon: '💉',
    metrics: {
      "자율신경 균형 (LF/HF)": isFinite(lfhf) ? lfhf.toFixed(2) : "N/A",
      "혈압 조절 능력 (VLF)": isFinite(vlf) ? vlf.toFixed(0) : "N/A",
      "평균 심박수": hrMed.toFixed(1) + " bpm"
    }
  };
}

/**
 * 모든 증상 예측
 */
export function predictAllSymptoms(windows: WindowData[], psd: PSDData): SymptomPredictions {
  return {
    stress: predictStress(windows, psd),
    breathing: predictBreathing(windows),
    pain: predictPain(windows, psd),
    depression: predictDepression(windows, psd),
    cardiovascular: predictCardiovascular(windows, psd),
    bloodPressure: predictBloodPressure(windows, psd)
  };
}

/**
 * 위험 수준 결정
 */
function getRiskLevel(score: number): 'low' | 'moderate' | 'high' | 'very_high' {
  if (score < 25) return 'low';
  if (score < 50) return 'moderate';
  if (score < 75) return 'high';
  return 'very_high';
}

/**
 * 신뢰도 계산
 */
function calculateConfidence(windowCount: number, targetCount: number): number {
  return Math.min(1.0, windowCount / targetCount);
}

/**
 * 데이터 부족 시 기본 응답
 */
function createInsufficientDataPrediction(symptomName: string, icon: string): SymptomPrediction {
  return {
    symptomName,
    riskLevel: 'low',
    score: 0,
    confidence: 0,
    indicators: ['symptom.indicator.insufficientData'],
    description: 'symptom.description.insufficientData',
    icon
  };
}

// === 설명 생성 함수들 ===

function getStressDescription(score: number, _indicators: string[]): string {
  if (score < 25) {
    return 'symptom.stress.description.low';
  } else if (score < 50) {
    return 'symptom.stress.description.moderate';
  } else if (score < 75) {
    return 'symptom.stress.description.high';
  } else {
    return 'symptom.stress.description.veryHigh';
  }
}

function getBreathingDescription(score: number, _respMed: number): string {
  if (score < 25) {
    return 'symptom.breathing.description.low';
  } else if (score < 50) {
    return 'symptom.breathing.description.moderate';
  } else if (score < 75) {
    return 'symptom.breathing.description.high';
  } else {
    return 'symptom.breathing.description.veryHigh';
  }
}

function getPainDescription(score: number): string {
  if (score < 25) {
    return 'symptom.pain.description.low';
  } else if (score < 50) {
    return 'symptom.pain.description.moderate';
  } else if (score < 75) {
    return 'symptom.pain.description.high';
  } else {
    return 'symptom.pain.description.veryHigh';
  }
}

function getDepressionDescription(score: number): string {
  if (score < 25) {
    return 'symptom.depression.description.low';
  } else if (score < 50) {
    return 'symptom.depression.description.moderate';
  } else if (score < 75) {
    return 'symptom.depression.description.high';
  } else {
    return 'symptom.depression.description.veryHigh';
  }
}

function getCardiovascularDescription(score: number): string {
  if (score < 25) {
    return 'symptom.cardiovascular.description.low';
  } else if (score < 50) {
    return 'symptom.cardiovascular.description.moderate';
  } else if (score < 75) {
    return 'symptom.cardiovascular.description.high';
  } else {
    return 'symptom.cardiovascular.description.veryHigh';
  }
}

function getBloodPressureDescription(score: number, lfhf: number): string {
  if (score < 25) {
    return 'symptom.bloodPressure.description.low';
  } else if (score < 50) {
    const trend = isFinite(lfhf) && lfhf > 1.5 ? 'High' : 'Low';
    return trend === 'High' ? 'symptom.bloodPressure.description.moderateHigh' : 'symptom.bloodPressure.description.moderateLow';
  } else if (score < 75) {
    return 'symptom.bloodPressure.description.high';
  } else {
    return 'symptom.bloodPressure.description.veryHigh';
  }
}

